import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Theme, ThemeMode, lightTheme, darkTheme } from '../theme';

interface ThemeContextType {
	theme: Theme;
	themeMode: ThemeMode;
	toggleTheme: () => void;
	setThemeMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// ローカルストレージのキー
const THEME_STORAGE_KEY = 'portfolio-theme-mode';

// OSのカラースキームを取得
const getSystemTheme = (): ThemeMode => {
	if (typeof window === 'undefined') return 'light';
	return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

// 保存されたテーマまたはシステムテーマを取得
const getSavedTheme = (): ThemeMode => {
	if (typeof window === 'undefined') return 'light';
	const saved = localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode | null;
	return saved || getSystemTheme();
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [themeMode, setThemeModeState] = useState<ThemeMode>(getSavedTheme());
	const theme = themeMode === 'dark' ? darkTheme : lightTheme;

	// テーマ変更時にローカルストレージに保存
	useEffect(() => {
		localStorage.setItem(THEME_STORAGE_KEY, themeMode);
		// bodyにdata属性を追加（CSS対応用）
		document.body.setAttribute('data-theme', themeMode);
	}, [themeMode]);

	// システムテーマの変更を監視
	useEffect(() => {
		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
		const handleChange = (e: MediaQueryListEvent) => {
			// 保存されたテーマがない場合のみシステムテーマに従う
			if (!localStorage.getItem(THEME_STORAGE_KEY)) {
				setThemeModeState(e.matches ? 'dark' : 'light');
			}
		};

		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, []);

	const toggleTheme = () => {
		setThemeModeState(prev => prev === 'light' ? 'dark' : 'light');
	};

	const setThemeMode = (mode: ThemeMode) => {
		setThemeModeState(mode);
	};

	return (
		<ThemeContext.Provider value={{ theme, themeMode, toggleTheme, setThemeMode }}>
			{children}
		</ThemeContext.Provider>
	);
};

export const useTheme = (): ThemeContextType => {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error('useTheme must be used within a ThemeProvider');
	}
	return context;
};
